import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { trackMetric } from '../../../core/metricUtils';
import { colors as vizColors } from '@tidepool/viz';

import { Box, Grid } from 'theme-ui';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import noop from 'lodash/noop';

import { bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import Button from '../../../components/elements/Button';
import Popover from '../../../components/elements/Popover';
import RadioGroup from '../../../components/elements/RadioGroup';
import useClinicMetricsPageName from '../useClinicMetricsPageName';

const getCgmUseFilterOptions = (t) => [
  { value: '<0.7', label: t('Less than 70%') },
  { value: '>=0.7', label: t('70% or more') },
];

const DropdownContent = ({
  onClose,
  onChange,
  timeCGMUsePercent,
}) => {
  const { t } = useTranslation();
  const pageName = useClinicMetricsPageName();
  const selectedClinicId = useSelector((state) => state.blip.selectedClinicId);

  const [pendingTimeCGMUsePercent, setPendingTimeCGMUsePercent] = useState(timeCGMUsePercent);

  const cgmUseFilterOptions = getCgmUseFilterOptions(t);

  const handleChange = timeCGMUsePercent => onChange(timeCGMUsePercent);

  return (
    <Box data-testid="cgm-use-filter-dropdown" mt={5} mx={2} sx={{ width: 300 }}>
      <Box>
        <Box sx={{ padding: 1, color: vizColors.gray50, lineHeight: 1, fontWeight: 'medium', fontSize: 1 }} mb={2}>
          {t('% CGM Use')}
        </Box>

        <Box sx={{ border: `1px solid ${vizColors.gray10}`, borderRadius: 6, padding: 2 }}>
          <RadioGroup
            id="cgm-use"
            name="cgm-use"
            options={cgmUseFilterOptions}
            variant="vertical"
            sx={{ fontSize: 0 }}
            value={pendingTimeCGMUsePercent}
            onChange={event => setPendingTimeCGMUsePercent(event.target.value || null)}
          />
        </Box>
      </Box>

      <Grid sx={{ gridTemplateColumns: '1fr 1fr' }} mt={3} mb={2}>
        <Button
          id="clear-cgm-use-filter"
          sx={{ fontSize: 1 }}
          variant="secondary"
          onClick={() => {
            trackMetric('Clinic - CGM use clear filter', { clinicId: selectedClinicId, pageName });
            setPendingTimeCGMUsePercent(null);
            handleChange(null);
            onClose();
          }}
        >
          {t('Clear')}
        </Button>

        <Button
          id="apply-cgm-use-filter"
          disabled={!pendingTimeCGMUsePercent}
          sx={{ fontSize: 1 }}
          variant="primary"
          onClick={() => {
            trackMetric('Clinic - CGM use apply filter', {
              clinicId: selectedClinicId,
              filter: pendingTimeCGMUsePercent,
              pageName,
            });

            handleChange(pendingTimeCGMUsePercent);
            onClose();
          }}
        >
          {t('Apply')}
        </Button>
      </Grid>
    </Box>
  );
};

const CGMUseFilterDropdown = ({
  onChange = noop,
  timeCGMUsePercent = null,
}) => {
  const { t } = useTranslation();
  const pageName = useClinicMetricsPageName();

  const cgmUsePopupFilterState = usePopupState({
    variant: 'popover',
    popupId: 'cgmUseFilters',
  });

  const selectedClinicId = useSelector((state) => state.blip.selectedClinicId);

  const handleCloseDropdown = () => cgmUsePopupFilterState.close();

  return (
    <>
      <Box
        onClick={() => {
          if (!cgmUsePopupFilterState.isOpen) trackMetric('Clinic - CGM Use filter open', { clinicId: selectedClinicId, pageName });
        }}
        sx={{ flexShrink: 0 }}
      >
        <Button
          variant="filter"
          id="cgm-use-filter-trigger"
          selected={!!timeCGMUsePercent}
          {...bindTrigger(cgmUsePopupFilterState)}
          icon={KeyboardArrowDownRoundedIcon}
          iconLabel="Filter by cgm use"
          sx={{ fontSize: 0, lineHeight: 1.3 }}
        >
          {t('% CGM Use')}
        </Button>
      </Box>

      <Popover
        minWidth="11em"
        closeIcon
        {...bindPopover(cgmUsePopupFilterState)}
        onClickCloseIcon={() => {
          trackMetric('Clinic - CGM Use filter close', { clinicId: selectedClinicId, pageName });
        }}
        onClose={handleCloseDropdown}
      >
        { cgmUsePopupFilterState.isOpen &&
          <DropdownContent
            timeCGMUsePercent={timeCGMUsePercent}
            onClose={handleCloseDropdown}
            onChange={onChange}
          />
        }
      </Popover>
    </>
  );
};

CGMUseFilterDropdown.propTypes = {
  onChange: PropTypes.func,
  timeCGMUsePercent: PropTypes.oneOf(getCgmUseFilterOptions(label => label).map(opt => opt.value)),
};

export default CGMUseFilterDropdown;
